import {BoxWithTitle} from './list';
import React, {useContext, useState} from 'react';
import {CustomDomainRequest} from '../../api/types/organization/CustomDomain';
import {useNavigate} from 'react-router-dom';
import {Input} from '../settings/profile/Profile';
import {CustomDomainScreenContext} from './base';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from '../../ui-components/Select';
import {StorageIcon} from '../RemoteStorage/screen';
import {ReactComponent as PerceptPixelLogo} from 'src/assets/icons/logo.svg';
import {Button} from '../../ui-components/Button/button';
import {toast} from 'react-toastify';
import {StorageType} from '../../api/types/organization/StorageSource';

const defaultSourceValue = 'default.' + Date.now();
type LoadingState = 'idle' | 'pending' | 'resolved' | 'rejected';

export default function CustomDomainAdd() {
    const {list, storageSourceList, api, onDomainAdd} = useContext(CustomDomainScreenContext);
    const [req, setReq] = useState<CustomDomainRequest>({
        hostname: '',
        org_storage_source: undefined,
    });
    const navigate = useNavigate();
    const [loading, setLoading] = useState<LoadingState>('idle');
    const [existingHostname, setExistingHostname] = useState<boolean>(false);

    return (
        <BoxWithTitle title={'Add Custom Domain'}>
            <div className={'flex flex-col p-6'}>
                <div className={'flex flex-col pb-10'}>
                    <span className={'pb-1.5 text-sm font-medium text-gray-700'}>Hostname</span>
                    <Input
                        type='text'
                        onChange={(e) => {
                            setReq({...req, hostname: e.target.value});
                            setExistingHostname(list.filter((d) => d.hostname === e.target.value).length > 0);
                        }}
                        value={req.hostname}
                    />
                    {existingHostname && <span className={'text-red-500'}>Custom Domain has already been added!</span>}
                </div>
                <div className={'flex flex-col pb-10'}>
                    <span className={'pb-1.5 text-sm font-medium text-gray-700'}>Storage</span>
                    <Select
                        value={req.org_storage_source}
                        onValueChange={(nv) => {
                            setReq({...req, org_storage_source: nv});
                        }}>
                        <SelectTrigger className='w-[180px]'>
                            <SelectValue placeholder='Select a Storage Source' />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectLabel>Storage Source</SelectLabel>
                                <SelectItem value={defaultSourceValue}>
                                    <div className={'flex flex-row items-center'}>
                                        <PerceptPixelLogo className={'h-4 w-4'} />
                                        <span className={'pl-2 italic'}>Default</span>
                                    </div>
                                </SelectItem>
                                {storageSourceList.map((s) => (
                                    <SelectItem key={s.name} value={s.name}>
                                        <div className={'flex flex-row items-center'}>
                                            <StorageIcon className={'h-4 w-4'} type={s.type} />
                                            <span className={'pl-2'}>{s.name}</span>
                                            {s.type === StorageType.REMOTE_STORAGE ? (
                                                <span className={'pl-2 text-xs italic text-gray-500'}>{s.origin}</span>
                                            ) : null}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>
                <div className={'flex flex-col pb-10'}>
                    <Button
                        data-disabled={
                            loading === 'pending' ||
                            req.hostname.length === 0 ||
                            req.org_storage_source === undefined ||
                            existingHostname
                        }
                        disabled={
                            loading === 'pending' ||
                            req.hostname.length === 0 ||
                            req.org_storage_source === undefined ||
                            existingHostname
                        }
                        className={'data-[disabled="true"]:bg-gray-300'}
                        onClick={() => {
                            setLoading('pending');
                            api
                                ?.add({
                                    hostname: req.hostname,
                                    org_storage_source:
                                        req.org_storage_source === defaultSourceValue
                                            ? undefined
                                            : req.org_storage_source,
                                })
                                .then((nd) => {
                                    setLoading('resolved');
                                    toast('Domain added. Please verify ownership.', {type: 'success'});
                                    onDomainAdd(nd);
                                    navigate(`../verify/${nd.hostname}`, {
                                        replace: true,
                                    });
                                })
                                .catch((err) => {
                                    setLoading('rejected');
                                    toast(`Failed to add domain: ${err.message}`, {type: 'error'});
                                });
                        }}>
                        {loading === 'pending' ? 'Adding...' : 'Add'}
                    </Button>
                </div>
            </div>
        </BoxWithTitle>
    );
}
